import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
    User, Heart, Phone, Mail, MapPin, Clock,
    Send, CheckCircle, Calendar, ChevronDown,
} from 'lucide-react';

/* ─── Pill radio button ──────────────────────────────────────────────────── */
const PillOption = ({ name, value, checked, onChange, children, required = false }) => (
    <label
        className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full border-2 cursor-pointer
            select-none font-medium text-sm transition-all duration-200
            ${checked
                ? 'border-granby-blue-light bg-granby-bg-blue text-granby-blue-dark shadow-sm'
                : 'border-gray-200 text-gray-500 hover:border-granby-blue/50 hover:bg-gray-50 hover:text-gray-700'
            }`}
    >
        <input
            type="radio" name={name} value={value}
            checked={checked} onChange={onChange} required={required}
            className="sr-only"
        />
        <span className={`w-2 h-2 rounded-full shrink-0 transition-all ${checked ? 'bg-granby-blue-dark' : 'bg-gray-300'}`} />
        {children}
    </label>
);

/* ─── Yes / No segmented toggle ──────────────────────────────────────────── */
const YesNoToggle = ({ name, state, onChange, required = false }) => (
    <div className="inline-flex rounded-xl overflow-hidden border-2 border-gray-200">
        {['Non', 'Oui'].map((v) => (
            <label
                key={v}
                className={`px-8 py-3 cursor-pointer font-semibold text-base select-none transition-all duration-200
                    ${state === v
                        ? v === 'Oui' ? 'bg-granby-blue text-white' : 'bg-gray-100 text-gray-800'
                        : 'text-gray-400 hover:bg-gray-50'
                    }`}
            >
                <input
                    type="radio" name={name} value={v}
                    checked={state === v} onChange={onChange} required={required}
                    className="sr-only"
                />
                {v}
            </label>
        ))}
    </div>
);

/* ─── Section card ───────────────────────────────────────────────────────── */
const SectionCard = ({ number, icon: Icon, title, subtitle, children }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white border border-gray-100 rounded-2xl shadow-granby overflow-hidden"
    >
        <div className="flex items-center gap-4 px-8 py-6 border-b border-gray-100 bg-gradient-to-r from-granby-bg-blue to-granby-bg-violet/40">
            <div className="w-10 h-10 rounded-full bg-granby-blue flex items-center justify-center text-white font-bold text-base shrink-0">
                {number}
            </div>
            <div>
                <h3 className="text-xl md:text-2xl font-athelas font-bold text-gray-900 flex items-center gap-2">
                    <Icon size={20} className="text-granby-blue-dark" aria-hidden="true" />
                    {title}
                </h3>
                {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
            </div>
        </div>
        <div className="px-8 py-8">{children}</div>
    </motion.div>
);

/* ─── Reusable field label ───────────────────────────────────────────────── */
const FieldLabel = ({ children, required }) => (
    <label className="block text-sm font-semibold text-gray-700 mb-2">
        {children}
        {required && <span className="text-granby-blue ml-1">*</span>}
    </label>
);

const inputCls =
    'w-full px-5 py-4 border-2 border-gray-200 rounded-xl bg-white ' +
    'focus:outline-none focus:border-granby-blue-light transition-colors ' +
    'text-gray-800 placeholder:text-gray-400';

/* ─── MAIN COMPONENT ─────────────────────────────────────────────────────── */
const Admission = () => {
    const [formData, setFormData] = useState({
        residentLastName: '',
        residentFirstName: '',
        residentDob: '',
        moveInDelay: '',
        contactLastName: '',
        contactFirstName: '',
        contactPhone: '',
        contactEmail: '',
        mobilityAid: '',
        neuro: '',
        neuroDetails: '',
        pets: '',
        allergies: '',
        dependencies: { smoking: '', alcohol: '', other: '', otherDetails: '' },
        avq: {},
        comments: '',
    });

    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [submitError, setSubmitError] = useState('');

    const handleChange = (e) => {
        const { name, value, type } = e.target;

        if (name.startsWith('dep_')) {
            const depKey = name.replace('dep_', '');
            setFormData((prev) => ({
                ...prev,
                dependencies: { ...prev.dependencies, [depKey]: value },
            }));
            return;
        }

        if (name.startsWith('avq_')) {
            const key = name.replace('avq_', '');
            setFormData((prev) => ({
                ...prev,
                avq: { ...prev.avq, [key]: value },
            }));
            return;
        }

        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? e.target.checked : value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setSubmitError('');
        try {
            const res = await fetch('/api/admission', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Erreur serveur');
            setIsSubmitted(true);
        } catch {
            setSubmitError('Une erreur est survenue. Veuillez réessayer.');
        } finally {
            setIsLoading(false);
        }
    };

    const contactInfo = [
        {
            icon: Phone,
            title: 'Téléphone',
            details: process.env.REACT_APP_PHONE_DISPLAY || '(450) 378-6374',
            description: 'Poste 2',
            textClass: 'text-granby-blue-light',
            bgClass: 'bg-granby-bg-blue',
            href: process.env.REACT_APP_PHONE_LINK || 'tel:14503786374',
            aria: 'Appeler la résidence',
        },
        {
            icon: Mail,
            title: 'Email',
            details: process.env.REACT_APP_EMAIL_DISPLAY || 'info@residencestantoine.ca',
            description: 'Réponse sous 48 h',
            textClass: 'text-granby-violet-light',
            bgClass: 'bg-granby-bg-violet',
            href: process.env.REACT_APP_EMAIL_MAILTO || 'mailto:info@residencestantoine.ca',
            aria: 'Écrire un courriel à la résidence',
        },
        {
            icon: MapPin,
            title: 'Adresse',
            details: process.env.REACT_APP_ADDRESS || '110 Rue Saint-Antoine S, Granby',
            description: 'Stationnement gratuit',
            textClass: 'text-granby-blue-light',
            bgClass: 'bg-granby-bg-blue',
            href: null,
            aria: null,
        },
        {
            icon: Clock,
            title: 'Heures de visite',
            details: '7 jours / 7 – 8h → 16h',
            description: 'Sur rendez-vous',
            textClass: 'text-granby-violet-light',
            bgClass: 'bg-granby-bg-violet',
            href: null,
            aria: null,
        },
    ];

    const mobilityOptions = [
        'Se déplace seul(e)',
        'Canne',
        'Marchette',
        'Déambulateur',
        'Fauteuil roulant',
    ];

    const avqItems = [
        'Toilette partielle matin/soir',
        'Prendre un bain',
        'Se nourrir',
        'Se vêtir',
        'Prendre / gérer les médicaments',
        'Continence',
    ];

    const moveInDelays = [
        'Immédiat – 2 sem.',
        '2 – 4 sem.',
        '1 – 2 mois',
        'Plus de 2 mois',
    ];

    return (
        <div className="font-sans text-[17px] text-gray-900">

            {/* ── HERO ──────────────────────────────────────────────────── */}
            <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                        backgroundImage:
                            'linear-gradient(135deg, rgba(166, 204, 230, 0.92), rgba(186, 174, 217, 0.92))',
                    }}
                    aria-hidden="true"
                />
                <div className="relative z-10 text-white text-center space-y-6 px-4">
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="text-5xl lg:text-6xl font-athelas font-bold"
                    >
                        Contact et Admission
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3, duration: 0.8 }}
                        className="text-xl max-w-3xl mx-auto text-white/90"
                    >
                        Débutez votre demande et notre équipe vous rappellera rapidement.
                    </motion.p>
                </div>
            </section>

            {/* ── CONTACT CARDS ─────────────────────────────────────────── */}
            <section className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
                    {contactInfo.map(({ icon: Icon, ...c }, i) => {
                        const Card = (
                            <div className="text-center space-y-2">
                                <div className={`w-20 h-20 rounded-xl flex items-center justify-center mx-auto mb-4 ${c.bgClass}`} aria-hidden="true">
                                    <Icon size={40} className={c.textClass} />
                                </div>
                                <h3 className="text-2xl font-athelas font-bold text-gray-900">{c.title}</h3>
                                <p className="font-semibold text-gray-800">{c.details}</p>
                                <p className="text-gray-600 text-base">{c.description}</p>
                            </div>
                        );
                        return (
                            <motion.div
                                key={c.title}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: i * 0.1 }}
                            >
                                {c.href ? (
                                    <a href={c.href} aria-label={c.aria || undefined}
                                        className="block rounded-2xl hover:shadow-granby-lg transition-shadow duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-granby-blue-light">
                                        {Card}
                                    </a>
                                ) : (
                                    <div className="rounded-2xl">{Card}</div>
                                )}
                            </motion.div>
                        );
                    })}
                </div>
            </section>

            {/* ── FORM ──────────────────────────────────────────────────── */}
            <section className="py-24 bg-gradient-to-b from-granby-bg-blue/50 to-white">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-14"
                    >
                        <h2 className="text-4xl md:text-5xl font-athelas font-bold text-gray-900 mb-3">
                            Demande d'admission
                        </h2>
                        <p className="text-gray-500 text-lg">
                            Remplissez ce formulaire et nous vous contacterons.
                        </p>
                    </motion.div>

                    {isSubmitted ? (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="text-center py-20 bg-white rounded-2xl shadow-granby"
                        >
                            <CheckCircle size={80} className="text-granby-blue-light mx-auto mb-6" aria-hidden="true" />
                            <h3 className="text-3xl font-athelas font-bold mb-4">Merci de nous considérer !</h3>
                            <p className="max-w-xl mx-auto text-gray-600">
                                Un membre de l'équipe vous contactera dans les <strong>48 h</strong>.
                            </p>
                        </motion.div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-8">

                            {/* ── SECTION 1 : Résident ───────────────────── */}
                            <SectionCard number="1" icon={User} title="Résident potentiel" subtitle="Informations personnelles">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                                    {[
                                        { label: 'Nom', name: 'residentLastName', placeholder: 'Nom de famille' },
                                        { label: 'Prénom', name: 'residentFirstName', placeholder: 'Prénom' },
                                    ].map((f) => (
                                        <div key={f.name}>
                                            <FieldLabel required>{f.label}</FieldLabel>
                                            <input
                                                type="text" name={f.name} required
                                                value={formData[f.name]} onChange={handleChange}
                                                placeholder={f.placeholder}
                                                className={inputCls}
                                            />
                                        </div>
                                    ))}

                                    {/* Date de naissance */}
                                    <div>
                                        <FieldLabel required>Date de naissance</FieldLabel>
                                        <div className="relative">
                                            <Calendar
                                                size={17}
                                                className="absolute left-4 top-1/2 -translate-y-1/2 text-granby-blue-light pointer-events-none z-10"
                                                aria-hidden="true"
                                            />
                                            <input
                                                type="date" name="residentDob" required
                                                value={formData.residentDob} onChange={handleChange}
                                                className={`${inputCls} pl-11`}
                                            />
                                        </div>
                                    </div>

                                    {/* Délai d'occupation */}
                                    <div>
                                        <FieldLabel required>Délai d'occupation souhaité</FieldLabel>
                                        <div className="relative">
                                            <ChevronDown
                                                size={17}
                                                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10"
                                                aria-hidden="true"
                                            />
                                            <select
                                                name="moveInDelay" required
                                                value={formData.moveInDelay} onChange={handleChange}
                                                className={`${inputCls} appearance-none pr-11 cursor-pointer`}
                                            >
                                                <option value="" disabled>— Sélectionnez —</option>
                                                {moveInDelays.map((d) => (
                                                    <option key={d}>{d}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </SectionCard>

                            {/* ── SECTION 2 : Responsable ────────────────── */}
                            <SectionCard number="2" icon={Phone} title="Personne responsable" subtitle="Coordonnées du contact principal">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {[
                                        { label: 'Nom', name: 'contactLastName', placeholder: 'Nom' },
                                        { label: 'Prénom', name: 'contactFirstName', placeholder: 'Prénom' },
                                        { label: 'Téléphone', name: 'contactPhone', placeholder: '(450) 000-0000', type: 'tel' },
                                        { label: 'Courriel', name: 'contactEmail', placeholder: 'contact@email.com', type: 'email' },
                                    ].map((f) => (
                                        <div key={f.name}>
                                            <FieldLabel required>{f.label}</FieldLabel>
                                            <input
                                                type={f.type || 'text'} name={f.name} required
                                                value={formData[f.name]} onChange={handleChange}
                                                placeholder={f.placeholder}
                                                className={inputCls}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </SectionCard>

                            {/* ── SECTION 3 : Soins ──────────────────────── */}
                            <SectionCard number="3" icon={Heart} title="Besoins de soins et services">

                                {/* Mobilité */}
                                <div className="mb-10">
                                    <p className="font-semibold text-gray-900 mb-4">
                                        Déplacement physique <span className="text-granby-blue-light">*</span>
                                    </p>
                                    <div className="flex flex-wrap gap-3">
                                        {mobilityOptions.map((opt) => (
                                            <PillOption
                                                key={opt} name="mobilityAid" value={opt} required
                                                checked={formData.mobilityAid === opt}
                                                onChange={handleChange}
                                            >
                                                {opt}
                                            </PillOption>
                                        ))}
                                    </div>
                                </div>

                                {/* Troubles neurocognitifs */}
                                <div className="mb-10">
                                    <p className="font-semibold text-gray-900 mb-4">
                                        Diagnostic de troubles neurocognitifs ? <span className="text-granby-blue-light">*</span>
                                    </p>
                                    <YesNoToggle name="neuro" state={formData.neuro} onChange={handleChange} required />
                                    {formData.neuro === 'Oui' && (
                                        <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            className="mt-4"
                                        >
                                            <input
                                                type="text" name="neuroDetails"
                                                value={formData.neuroDetails} onChange={handleChange}
                                                placeholder="Précisez le diagnostic"
                                                className={inputCls}
                                                required
                                            />
                                        </motion.div>
                                    )}
                                </div>

                                {/* Dépendances */}
                                <div className="mb-10">
                                    <p className="font-semibold text-gray-900 mb-5">
                                        Dépendances connues <span className="text-granby-blue-light">*</span>
                                    </p>
                                    <div className="space-y-5">
                                        {[
                                            ['smoking', 'Fumeur / Fumeuse'],
                                            ['alcohol', 'Alcool'],
                                            ['other', 'Autres'],
                                        ].map(([k, lbl]) => (
                                            <div key={k} className="flex flex-wrap items-center gap-6">
                                                <span className="w-44 text-gray-700 font-medium">{lbl}</span>
                                                <YesNoToggle
                                                    name={`dep_${k}`}
                                                    state={formData.dependencies[k]}
                                                    onChange={handleChange}
                                                    required
                                                />
                                                {k === 'other' && formData.dependencies.other === 'Oui' && (
                                                    <motion.div
                                                        initial={{ opacity: 0, width: 0 }}
                                                        animate={{ opacity: 1, width: 'auto' }}
                                                        className="flex-1 min-w-[200px]"
                                                    >
                                                        <input
                                                            type="text" name="dep_otherDetails"
                                                            value={formData.dependencies.otherDetails}
                                                            onChange={(e) =>
                                                                setFormData((p) => ({
                                                                    ...p,
                                                                    dependencies: { ...p.dependencies, otherDetails: e.target.value },
                                                                }))
                                                            }
                                                            placeholder="Précisez"
                                                            className={inputCls}
                                                            required
                                                        />
                                                    </motion.div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* AVQ */}
                                <div className="mb-10">
                                    <p className="font-semibold text-gray-900 mb-2">
                                        Activités de vie quotidienne <span className="text-granby-blue-light">*</span>
                                    </p>
                                    <p className="text-sm text-gray-500 mb-5">Oui = aide nécessaire</p>
                                    <div className="divide-y divide-gray-100 border border-gray-100 rounded-xl overflow-hidden">
                                        {avqItems.map((item, i) => (
                                            <div
                                                key={item}
                                                className={`flex flex-wrap items-center justify-between gap-4 px-5 py-4 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/60'}`}
                                            >
                                                <span className="text-gray-800 font-medium">{item}</span>
                                                <YesNoToggle
                                                    name={`avq_${item}`}
                                                    state={formData.avq[item]}
                                                    onChange={handleChange}
                                                    required
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Divers */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
                                    <div>
                                        <p className="font-semibold text-gray-900 mb-4">
                                            Animal de compagnie ? <span className="text-granby-blue-light">*</span>
                                        </p>
                                        <YesNoToggle name="pets" state={formData.pets} onChange={handleChange} required />
                                    </div>
                                    <div>
                                        <FieldLabel required>Allergies connues</FieldLabel>
                                        <input
                                            type="text" name="allergies"
                                            value={formData.allergies} onChange={handleChange}
                                            placeholder="Aucune / Précisez"
                                            className={inputCls}
                                            required
                                        />
                                    </div>
                                </div>

                                {/* Commentaires */}
                                <div>
                                    <FieldLabel>Demandes spéciales / Commentaires</FieldLabel>
                                    <textarea
                                        name="comments" rows={4}
                                        value={formData.comments} onChange={handleChange}
                                        placeholder="Toute information pertinente…"
                                        className={`${inputCls} resize-none`}
                                    />
                                </div>
                            </SectionCard>

                            {/* ── SUBMIT ─────────────────────────────────── */}
                            <motion.button
                                type="submit"
                                disabled={isLoading}
                                whileHover={{ scale: isLoading ? 1 : 1.01 }}
                                whileTap={{ scale: isLoading ? 1 : 0.99 }}
                                className="w-full py-5 px-6 rounded-xl bg-granby-blue text-white font-semibold text-xl
                                    flex items-center justify-center gap-3 shadow-granby hover:shadow-granby-lg
                                    transition-shadow duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-granby-blue-light
                                    disabled:opacity-70 disabled:cursor-not-allowed"
                            >
                                <Send size={22} aria-hidden="true" />
                                {isLoading ? 'Envoi en cours…' : 'Envoyer la demande'}
                            </motion.button>
                            {submitError && (
                                <p className="text-red-500 text-sm text-center mt-2">{submitError}</p>
                            )}

                        </form>
                    )}
                </div>
            </section>
        </div>
    );
};

export default Admission;
